import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { AlertTriangle, CheckCircle, XCircle, ChevronRight, RotateCcw, Target } from 'lucide-react';
import { scenarios } from '../data/mock';
import { recordScenarioAttempt } from '../services/api';

const InteractiveScenario = () => {
  const [currentScenario, setCurrentScenario] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [correctAnswers, setCorrectAnswers] = useState(0);

  const scenario = scenarios[currentScenario];

  const handleOptionSelect = async (index) => {
    if (showFeedback) return;
    setSelectedOption(index);
    setShowFeedback(true);
    
    const isCorrect = scenario.options[index].correct;
    if (isCorrect) {
      setCorrectAnswers(prev => prev + 1);
    }

    // Record attempt to backend if user is enrolled
    const leadId = localStorage.getItem('oshaLeadId');
    if (leadId) {
      try {
        await recordScenarioAttempt({
          lead_id: leadId,
          scenario_id: scenario.id,
          selected_option: index,
          is_correct: isCorrect
        });
      } catch (error) {
        console.error('Error recording scenario attempt:', error);
        // Don't block the user, just log the error
      }
    }
  };

  const handleNext = () => {
    if (currentScenario < scenarios.length - 1) {
      setCurrentScenario(prev => prev + 1);
      setSelectedOption(null);
      setShowFeedback(false);
    }
  };

  const handleReset = () => {
    setCurrentScenario(0);
    setSelectedOption(null);
    setShowFeedback(false);
    setCorrectAnswers(0);
  };

  const isLastScenario = currentScenario === scenarios.length - 1;

  return (
    <section className="py-20 bg-gradient-to-b from-white to-slate-50">
      <div className="container mx-auto px-6">
        {/* Section header */}
        <div className="text-center mb-12">
          <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">Learn By Doing</span>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2 mb-4">
            Interactive Safety Scenarios
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Test your knowledge with real-world workplace situations. Make the right call!
          </p>
        </div>

        {/* Progress indicator */}
        <div className="max-w-3xl mx-auto mb-8">
          <div className="flex items-center justify-between text-sm text-slate-600 mb-2">
            <span>Scenario {currentScenario + 1} of {scenarios.length}</span>
            <span className="flex items-center gap-1">
              <Target className="w-4 h-4 text-emerald-500" />
              Score: {correctAnswers}/{currentScenario + (showFeedback ? 1 : 0)}
            </span>
          </div>
          <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all duration-500"
              style={{ width: `${((currentScenario + (showFeedback ? 1 : 0)) / scenarios.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Scenario card */}
        <Card className="max-w-3xl mx-auto p-8 shadow-xl border-0 bg-white rounded-2xl">
          {/* Scenario header */}
          <div className="flex items-start gap-4 mb-6">
            <div className="w-14 h-14 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-7 h-7 text-amber-600" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-900">{scenario.title}</h3>
              <p className="text-slate-600 mt-1">{scenario.description}</p>
            </div>
          </div>

          {/* Situation box */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-6">
            <p className="text-slate-700 font-medium">Situation:</p>
            <p className="text-slate-600 mt-1">{scenario.situation}</p>
          </div>

          {/* Options */}
          <div className="space-y-3 mb-6">
            <p className="font-semibold text-slate-900">What would you do?</p>
            {scenario.options.map((option, index) => {
              const isSelected = selectedOption === index;
              const isCorrect = option.correct;
              
              let optionClass = 'border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/50';
              if (showFeedback) {
                if (isCorrect) {
                  optionClass = 'border-emerald-500 bg-emerald-50';
                } else if (isSelected && !isCorrect) {
                  optionClass = 'border-red-500 bg-red-50';
                } else {
                  optionClass = 'border-slate-200 opacity-50';
                }
              }

              return (
                <div
                  key={index}
                  onClick={() => handleOptionSelect(index)}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 ${optionClass} ${
                    !showFeedback ? 'hover:-translate-y-0.5' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      showFeedback && isCorrect ? 'border-emerald-500 bg-emerald-500' :
                      showFeedback && isSelected && !isCorrect ? 'border-red-500 bg-red-500' :
                      'border-slate-300'
                    }`}>
                      {showFeedback && isCorrect && <CheckCircle className="w-4 h-4 text-white" />}
                      {showFeedback && isSelected && !isCorrect && <XCircle className="w-4 h-4 text-white" />}
                    </div>
                    <span className={`${
                      showFeedback && isCorrect ? 'text-emerald-700 font-medium' :
                      showFeedback && isSelected && !isCorrect ? 'text-red-700' :
                      'text-slate-700'
                    }`}>
                      {option.text}
                    </span>
                  </div>
                  
                  {/* Feedback */}
                  {showFeedback && isSelected && (
                    <div className={`mt-3 pt-3 border-t ${
                      isCorrect ? 'border-emerald-200' : 'border-red-200'
                    }`}>
                      <p className={`text-sm ${isCorrect ? 'text-emerald-600' : 'text-red-600'}`}>
                        {option.feedback}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Navigation buttons */}
          <div className="flex justify-between items-center pt-4 border-t border-slate-100">
            <Button
              variant="outline"
              onClick={handleReset}
              className="text-slate-600"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Start Over
            </Button>
            
            {showFeedback && (
              <Button
                onClick={isLastScenario ? handleReset : handleNext}
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
              >
                {isLastScenario ? 'Try Again' : 'Next Scenario'}
                {!isLastScenario && <ChevronRight className="w-4 h-4 ml-1" />}
              </Button>
            )}
          </div>
        </Card>
      </div>
    </section>
  );
};

export default InteractiveScenario;
